import Link from "next/link";
import { Container, Row, Col } from "react-bootstrap";
import {
  FaArrowLeft,
  FaAngleLeft,
  FaAngleRight,
  FaRegCalendarAlt,
  FaEye,
  FaTag,
} from "react-icons/fa";
import { LayoutOne } from "@/layouts";
import BlogSideBar from "@/components/blog/sidebar";
import SeoHead from "@/components/SeoHead";
import PageViewBeacon from "@/components/PageViewBeacon";
import OurGroupSection from "@/components/ourGroup";
import FaqSection from "@/components/faq";
import {
  getAllBlogSlugs,
  getBlogBySlug,
  getLatestBlogs,
  getPosts,
} from "@/lib/blog";
import { getCategories, getTags, getAuthors } from "@/lib/cms";
import { buildBlogPostSchema } from "@/lib/blogPostSchema";

export async function getStaticPaths() {
  const slugs = await getAllBlogSlugs();
  return {
    paths: slugs.map((s) => ({ params: s })),
    fallback: "blocking",
  };
}

export async function getStaticProps({ params }) {
  const slug = String(params.slug || "");
  const [blog, allPosts, latest, categories, tags, authors] = await Promise.all([
    getBlogBySlug(slug),
    getPosts({ perPage: 100 }),
    getLatestBlogs(4),
    getCategories(),
    getTags(),
    getAuthors(),
  ]);
  if (!blog) return { notFound: true, revalidate: 60 };

  const idx = allPosts.data.findIndex((b) => b.slug === slug);
  const prevPost = idx > 0 ? allPosts.data[idx - 1] : null;
  const nextPost =
    idx >= 0 && idx < allPosts.data.length - 1 ? allPosts.data[idx + 1] : null;

  return {
    props: {
      blog,
      prevPost: prevPost ? { slug: prevPost.slug, title: prevPost.title } : null,
      nextPost: nextPost ? { slug: nextPost.slug, title: nextPost.title } : null,
      latest,
      categories,
      tags,
      authors,
    },
    revalidate: 3600,
  };
}

function BlogDetails({ blog, prevPost, nextPost, latest = [], categories = [], tags = [], authors = [] }) {
  if (!blog) {
    return (
      <LayoutOne topbar={true} banner={true} showHeading={false}>
        <div className="ltn__blog-area mb-120">
          <Container>
            <Row className="justify-content-center">
              <Col xs={12} lg={8} className="text-center py-5">
                <h2>Post not found</h2>
                <p>The post you're looking for doesn't exist.</p>
                <Link href="/blog" className="btn theme-btn-1 mt-3">
                  <FaArrowLeft /> Back to blog
                </Link>
              </Col>
            </Row>
          </Container>
        </div>
      </LayoutOne>
    );
  }

  const postTags = Array.isArray(blog.tag) ? blog.tag : [];
  const postCategories = Array.isArray(blog.category_names) ? blog.category_names : [];
  const secondaryKeywords = Array.isArray(blog.secondary_keywords)
    ? blog.secondary_keywords
    : [];
  const adminFaqs = Array.isArray(blog.faqs)
    ? blog.faqs.filter((item) => item?.question && item?.answer)
    : [];
  const faqTopic = String(blog.title || "Nagpur Property")
    .split(/[:|]/)[0]
    .trim();

  const featuredImg =
    blog.featured_image ||
    (blog.thumbImg ? `/img/blog/${blog.thumbImg}` : "");

  // ─── SEO meta (rendered via SeoHead component) ───
  // Pass the full structured author object (name, bio, credentials, avatar,
  // sameAs) so the head emits a Person JSON-LD block for E-E-A-T signals.
  // SeoHead falls back to the string `authorName` for backward compatibility.
  const authorObj =
    blog.author && typeof blog.author === "object"
      ? blog.author
      : { name: blog.author || "Admin" };
  const authorName = authorObj.name || "Admin";
  // Surface the real DB role (super_admin / editor / author / seo_manager)
  // with a friendly label — falls back to "Author" for legacy data.
  const authorRoleMap = {
    super_admin: "Super Admin",
    editor: "Editor",
    author: "Author",
    seo_manager: "SEO Manager",
  };
  const authorRoleLabel =
    authorRoleMap[authorObj.role] || authorObj.role || "Author";

  return (
    <>
      <PageViewBeacon
        contentType="post"
        contentId={blog.id}
        slug={blog.slug}
        path={`/blog/${blog.slug}`}
      />
      <LayoutOne
        topbar={true}
        showHeading={false}
        banner={true}
        bannerImage={featuredImg}
        bannerTitle={blog.title}
      >
        <SeoHead
        seo={{
          ...blog,
          schema_json: buildBlogPostSchema(blog),
        }}
        type="article"
        path={`/blog/${blog.slug}`}
        fallbackTitle={blog.title}
        fallbackDescription={blog.fullDescription || blog.shortDescription}
        fallbackImage={featuredImg}
        extraKeywords={[]}
        publishedTime={blog.published_at}
        author={authorObj}
        authorName={authorName}
        sectionName={postCategories[0]}
        tagNames={postTags}
        publisherName="Siddhivinayak Group"
      />

      <div className="ltn__blog-area mb-120">
        <Container>
          <Row>
            <Col xs={12} lg={8}>
              <article className="blog-detail">
                <div>
                  <div className="blog-detail__body">
                    <header className="blog-detail__header">
                      <h1 className="blog-detail__title">{blog.title}</h1>
                      <ul className="blog-detail__meta">
                        {blog.author && blog.author.name ? (
                          <li className="blog-detail__meta-author">
                            {blog.author.name}
                          </li>
                        ) : null}
                        {blog.date ? (
                          <li>
                            <FaRegCalendarAlt /> {blog.date}
                          </li>
                        ) : null}
                        {blog.views ? (
                          <li>
                            <FaEye /> {blog.views} Views
                          </li>
                        ) : null}
                      </ul>
                    </header>

                    <div className="blog-detail__content">
                      {blog.fullDescription ? (
                        <p>{blog.fullDescription}</p>
                      ) : null}

                      {blog.content ? (
                        <div
                          className="blog-detail__html"
                          dangerouslySetInnerHTML={{ __html: blog.content }}
                        />
                      ) : null}

                    </div>

                    {adminFaqs.length ? (
                      <FaqSection
                        sectionSpace="pt-40 pb-40"
                        faqData={adminFaqs}
                        subTitle=""
                        title={`${faqTopic} FAQs`}
                        intro=""
                        singleColumn={true}
                        embedded={true}
                      />
                    ) : null}

                    {postTags.length ? (
                      <div className="blog-detail__tags">
                        <span>
                          <FaTag /> Tags:
                        </span>
                        {postTags.map((tag, idx) => (
                          <Link
                            key={idx}
                            href={`/tag/${String(tag).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          >
                            {tag}
                          </Link>
                        ))}
                      </div>
                    ) : null}

                    {blog.author && blog.author.name ? (
                      <div className="blog-detail__author">
                        <div>
                          <h2 className="blog-detail__author-name">
                            {blog.author.name}
                          </h2>
                          <p className="blog-detail__author-role">{authorRoleLabel}</p>
                          {blog.author.description ? (
                            <div
                              className="blog-detail__author-bio"
                              dangerouslySetInnerHTML={{ __html: blog.author.description }}
                            />
                          ) : null}
                        </div>
                      </div>
                    ) : null}

                    <div className="blog-detail__nav">
                      <Link href="/blog" className="blog-detail__nav-back">
                        <FaArrowLeft /> Back to all posts
                      </Link>
                      <div className="blog-detail__nav-pager">
                        {prevPost ? (
                          <Link
                            href={`/blog/${prevPost.slug}`}
                            className="blog-detail__nav-prev"
                          >
                            <FaAngleLeft /> Prev
                          </Link>
                        ) : null}
                        {nextPost ? (
                          <Link
                            href={`/blog/${nextPost.slug}`}
                            className="blog-detail__nav-next"
                          >
                            Next <FaAngleRight />
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </Col>

            <Col xs={12} lg={{ span: 4, order: 0 }}>
              <BlogSideBar
                latestdBlogs={latest}
                categories={categories}
                tags={tags}
                authors={authors}
                showAgent={false}
                showBookForm={false}
                showTopRated={false}
                showAuthors={false}
                showTwitter={false}
                showFollowUs={false}
              />
            </Col>
          </Row>
        </Container>
      </div>

      <div className="section-bg-1">
        <OurGroupSection />
      </div>
      </LayoutOne>
    </>
  );
}

export default BlogDetails;
